import { useAuthStore } from '../store/authStore';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Spinner } from '../components/ui/spinner';
import NewAlerts from './AlertsDashboard/components/NewAlerts';
import axiosClient from '../lib/auth';
import { useAlertSound } from '../hooks/useAlertSound';
import { useGamepadVibration } from '../hooks/useGamepadVibration';
import { useEffect, useState } from 'react';

import { Loader2, ShieldAlert } from 'lucide-react';

import AssignedAlerts from '@/app/AlertsDashboard/components/AssignedAlerts';
import ResolvedAlerts from '@/app/AlertsDashboard/components/ResolvedAlerts';

import { useQuery } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { plural } from '@/lib/utils/pluralize';
import { useFeatureFlagEnabled } from 'posthog-js/react';

interface DashboardData {
  openAlerts: number;
  assigned: number;
  unAssigned: number;
  currentSlaBreach: number;
  aboutToBreachSla: number;
  success: boolean;
  message: string | null;
  code: string;
  errors: string | null;
}

const tabs = ['new', 'assigned', 'resolved'] as const;
type TabValue = (typeof tabs)[number];

function App() {
  const auth = useAuthStore((state) => state);
  const [activeTab, setActiveTab] = useState<TabValue>('new');
  const [isUsingGamepad, setIsUsingGamepad] = useState(false);
  const [lastPressTime, setLastPressTime] = useState(0);
  const [assignedCountOverride, setAssignedCountOverride] = useState<number>();
  const [unassignedCountOverride, setUnassignedCountOverride] = useState<number>();
  const dashboardV2Enabled = useFeatureFlagEnabled('dashboard-v2');
  const dashboardQuery = useQuery<DashboardData>({
    queryKey: ['dashboardData'],
    queryFn: async () => {
      const response = await axiosClient.get(dashboardV2Enabled ? `/AlertMonitoring/dashboard-v2?type=${activeTab === 'new' ? 'assigned' : 'unassigned' }` : `/AlertMonitoring/dashboard`);
      return response.data;
    },
    gcTime: 0,
    staleTime: 0,
    refetchInterval: 10000, // Refetch every 10 seconds
    refetchIntervalInBackground: false, // Continue refetching even when tab is in background
  });
  const { data: dashboardData, error } = dashboardQuery;
  const assignedCount = typeof dashboardData?.assigned === 'number' ? dashboardData?.assigned : assignedCountOverride ?? 0;
  const unassignedCount = typeof dashboardData?.unAssigned === 'number' ? dashboardData?.unAssigned : unassignedCountOverride ?? 0;

  useAlertSound(unassignedCount > 0);
  useGamepadVibration(unassignedCount > 0);

  // Handle circular tab navigation
  const handleTabChange = (direction: 'prev' | 'next') => {
    const now = Date.now();
    // Debounce the input to prevent rapid state updates
    if (now - lastPressTime < 200) return;
    setLastPressTime(now);

    const currentIndex = tabs.indexOf(activeTab);
    let newIndex: number;

    if (direction === 'prev') {
      // L2 moves left (to previous tab)
      newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
    } else {
      // R2 moves right (to next tab)
      newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
    }

    setActiveTab(tabs[newIndex]);
  };

  useEffect(() => {
    let lastButtonState = { l2: false, r2: false };

    const handleGamepadInput = (e: GamepadEvent) => {
      const gamepad = e.gamepad;
      if (!gamepad) return;

      // L2 is button 4, R2 is button 5 in standard gamepad mapping
      const l2Pressed = gamepad.buttons[4]?.pressed;
      const r2Pressed = gamepad.buttons[5]?.pressed;

      // Only trigger on button press, not hold
      if (l2Pressed && !lastButtonState.l2) {
        setIsUsingGamepad(true);
        handleTabChange('prev');
      } else if (r2Pressed && !lastButtonState.r2) {
        setIsUsingGamepad(true);
        handleTabChange('next');
      }

      lastButtonState = { l2: l2Pressed, r2: r2Pressed };
    };

    const checkGamepad = () => {
      const gamepads = navigator.getGamepads();
      const gamepad = gamepads[0]; // PS5 controller usually connects as first gamepad
      if (gamepad) {
        handleGamepadInput({ gamepad } as GamepadEvent);
      }
    };

    // Check gamepad input every frame
    const intervalId = setInterval(checkGamepad, 16); // ~60fps

    // Set initial cursor state
    if (isUsingGamepad) {
      document.body.style.cursor = 'none';
    }

    return () => {
      clearInterval(intervalId);
      document.body.style.cursor = 'auto';
    };
  }, [activeTab]);

  if (error) {
    console.error('Error fetching dashboard data:', error);
  }

  if (!auth.user) {
    return (
      <div className="ml-[-120px] flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab as (value: string) => void}
      className="w-full"
    >
      <div className="flex justify-between border-b border-[#2E3835]" data-onboarding="alert-tabs">
        <TabsList className="flex-1 border-none bg-inherit">
          <TabsTrigger value="new" data-onboarding="alert-tab-new" className="flex-1 gap-2 text-[#B7D257] hover:bg-card2">
            <p className="font-semibold">Alerts</p>
            {!dashboardQuery.isLoading ? (
              <p
                className={cn(
                  'h-4 rounded-sm bg-red-900 px-1 text-xs font-semibold leading-4 text-white',
                  { 'bg-green-light': unassignedCount === 0 }
                )}
              >
                {unassignedCount}
              </p>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
          </TabsTrigger>
          <TabsTrigger value="assigned" data-onboarding="alert-tab-assigned" className="flex-1 gap-2 text-[#B7D257] hover:bg-card2">
            <p className="font-semibold">Assigned</p>
            {!dashboardQuery.isLoading ? (
              <p
                className={cn(
                  'h-4 rounded-sm bg-red-900 px-1 text-xs font-semibold leading-4 text-white',
                  { 'bg-green-light': assignedCount === 0 }
                )}
              >
                {assignedCount}
              </p>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved" data-onboarding="alert-tab-resolved" className="flex-1 gap-2 text-[#B7D257] hover:bg-card2">
            <p className="font-semibold">Resolved</p>
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="new">
           

        {!dashboardQuery.isLoading && (
          <h2 className="flex items-center p-4 text-3xl font-semibold text-white">
            {`${unassignedCount} ${plural('New Alert', unassignedCount)}`}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="ml-auto flex items-center gap-2 rounded-full border border-[#2E3835] bg-card2 px-2 py-1.5 text-sm font-semibold">
                    <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
                    <span>Monitoring Active</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="border-[#2E3835] bg-[#111] text-white">
                  <p>The Watchful platform is actively analyzing and categorizing your alerts</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </h2>
        )}
      
        <div data-onboarding="new-alerts">
          <NewAlerts type="new" setUnassignedCountOverride={setUnassignedCountOverride} />
        </div>
      </TabsContent>
      <TabsContent value="assigned">
        {!dashboardQuery.isLoading && (
          <h2 className="flex items-center p-4 text-3xl font-semibold text-white">
            {`${assignedCount} ${plural('Assigned Alert', assignedCount)}`}
            <Loader2 className="ml-auto h-7 w-7 animate-spin text-[#B7D257]" />
          </h2>
        )}
        <div data-onboarding="assigned-alerts">
          <AssignedAlerts setAssignedCountOverride={setAssignedCountOverride} />
        </div>
      </TabsContent>
      <TabsContent value="resolved">
        <ResolvedAlerts  />
      </TabsContent>
    </Tabs>
  );
}

export default App;
